import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Trash2, 
  X,
  UserCheck,
  UserX,
  School,
  GraduationCap,
  Mail,
  CreditCard,
  Clock,
  CheckSquare,
  Square
} from 'lucide-react';
import participantesService from '../services/participantesService';
import { getCurrentUserProfile } from '../services/usuariosService';
import '../styles/participantes.css';

const Participantes = ({ teste, onClose }) => {
  const [participantes, setParticipantes] = useState([]);
  const [alunosDisponiveis, setAlunosDisponiveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Estados para filtros de alunos disponíveis
  const [filters, setFilters] = useState({
    search: '',
    escola_id: '',
    ano_escolar_id: ''
  });

  // Estados para seleção múltipla
  const [selectedAlunos, setSelectedAlunos] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Estados para dropdowns
  const [escolas, setEscolas] = useState([]);
  const [anosEscolares, setAnosEscolares] = useState([]);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);

  useEffect(() => {
    if (teste && teste.id) {
      loadInitialData();
    }
  }, [teste]);

  // Recarregar alunos quando filtros mudarem
  useEffect(() => {
    if (teste && teste.id) {
      loadAlunosDisponiveis();
    }
  }, [filters]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Carregar perfil do usuário logado
      const profileResult = await getCurrentUserProfile();
      if (profileResult.success) {
        setCurrentUserProfile(profileResult.data);
      }
      
      const [participantesData, escolasData, anosData] = await Promise.all([
        participantesService.getByTesteId(teste.id),
        participantesService.getEscolas(),
        participantesService.getAnosEscolares()
      ]);

      setParticipantes(participantesData);
      
      // Filtrar escolas baseado no perfil do usuário
      if (profileResult.success && profileResult.data?.school_adm && profileResult.data?.school_id) {
        const escolaDoUsuario = escolasData.filter(escola => escola.id === profileResult.data.school_id);
        setEscolas(escolaDoUsuario);
      } else {
        setEscolas(escolasData);
      }
      
      setAnosEscolares(anosData);
    } catch (error) {
      setError('Erro ao carregar dados: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAlunosDisponiveis = async () => {
    try {
      let filtrosComEscola = { ...filters };
      
      // Se for school admin, forçar filtro pela escola do usuário
      if (currentUserProfile?.school_adm && currentUserProfile?.school_id) {
        filtrosComEscola.escola_id = currentUserProfile.school_id;
      }
      // Se for super admin e tiver escola selecionada no dropdown, usar essa escola
      else if (filters.escola_id) {
        filtrosComEscola.escola_id = filters.escola_id;
      }
      
      console.log('🔍 Filtros aplicados:', filtrosComEscola);
      const alunos = await participantesService.getAlunosDisponiveis(teste.id, filtrosComEscola);
      console.log('👥 Alunos retornados:', alunos.length);
      setAlunosDisponiveis(alunos);
      setSelectedAlunos([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Erro ao carregar alunos disponíveis:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      escola_id: '',
      ano_escolar_id: ''
    });
  };

  const handleSelectAluno = (alunoId) => {
    setSelectedAlunos(prev => {
      if (prev.includes(alunoId)) {
        return prev.filter(id => id !== alunoId);
      } else {
        return [...prev, alunoId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedAlunos([]);
    } else {
      setSelectedAlunos(alunosDisponiveis.map(aluno => aluno.id));
    }
    setSelectAll(!selectAll);
  };

  const handleAddParticipantes = async () => {
    if (selectedAlunos.length === 0) {
      setError('Selecione pelo menos um aluno');
      return;
    }

    try {
      setLoading(true);
      
      if (selectedAlunos.length === 1) {
        const novoParticipante = await participantesService.create(teste.id, selectedAlunos[0]);
        setParticipantes(prev => [novoParticipante, ...prev]);
        setSuccess('Participante adicionado com sucesso!');
      } else {
        const novosParticipantes = await participantesService.createMultiple(teste.id, selectedAlunos);
        setParticipantes(prev => [...novosParticipantes, ...prev]);
        setSuccess(`${novosParticipantes.length} participantes adicionados com sucesso!`);
      }

      setShowAddModal(false);
      setSelectedAlunos([]);
      loadAlunosDisponiveis();
    } catch (error) {
      setError('Erro ao adicionar participantes: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveParticipante = async (participante) => {
    if (!window.confirm(`Remover ${participante.alunos.nome} dos participantes?`)) {
      return;
    }

    try {
      setLoading(true);
      await participantesService.delete(participante.id);
      setParticipantes(prev => prev.filter(p => p.id !== participante.id));
      setSuccess('Participante removido com sucesso!');
      loadAlunosDisponiveis();
    } catch (error) {
      setError('Erro ao remover participante: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (participante) => {
    try {
      const updatedParticipante = await participantesService.toggleActive(
        participante.id, 
        !participante.ativo
      );
      setParticipantes(prev => 
        prev.map(p => p.id === participante.id ? updatedParticipante : p)
      );
      setSuccess(`Participante ${updatedParticipante.ativo ? 'ativado' : 'desativado'} com sucesso!`);
    } catch (error) {
      setError('Erro ao alterar status: ' + error.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  return (
    <div className="participantes-container">
      {/* Header */}
      <div className="participantes-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title">
              <Users size={24} />
              Participantes do Teste
            </h1>
            <p className="page-subtitle">
              {teste.nome} - {participantes.length} participante(s)
            </p>
          </div>
          <div className="header-actions">
            <button 
              className="btn btn-primary" 
              onClick={() => setShowAddModal(true)}
              disabled={loading}
            >
              <Plus size={20} />
              Adicionar Participantes
            </button>
            <button className="btn btn-secondary" onClick={onClose}>
              <X size={20} />
              Voltar
            </button>
          </div>
        </div>
      </div>

      {/* Mensagens */}
      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError('')} className="alert-close">×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess('')} className="alert-close">×</button>
        </div>
      )}

      {/* Lista de Participantes */}
      <div className="content-card">
        {loading && participantes.length === 0 ? (
          <div className="loading">Carregando participantes...</div>
        ) : participantes.length === 0 ? (
          <div className="empty-state">
            <Users size={48} />
            <h3>Nenhum participante adicionado</h3>
            <p>Adicione alunos para que possam realizar este teste.</p>
            <button 
              className="btn btn-primary" 
              onClick={() => setShowAddModal(true)}
            >
              <Plus size={20} />
              Adicionar Primeiro Participante
            </button>
          </div>
        ) : (
          <div className="participantes-grid">
            {participantes.map(participante => (
              <div 
                key={participante.id} 
                className={`participante-card ${!participante.ativo ? 'inactive' : ''}`}
              >
                <div className="participante-header">
                  <div className="participante-info">
                    <h3>{participante.alunos.nome}</h3>
                    <div className="info-badges">
                      {participante.ativo ? (
                        <span className="badge success">
                          <UserCheck size={14} />
                          Ativo
                        </span>
                      ) : (
                        <span className="badge error">
                          <UserX size={14} />
                          Inativo
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="participante-actions">
                    <button
                      className="btn-icon"
                      onClick={() => handleToggleActive(participante)}
                      title={participante.ativo ? 'Desativar' : 'Ativar'}
                    >
                      {participante.ativo ? <UserX size={16} /> : <UserCheck size={16} />}
                    </button>
                    <button
                      className="btn-icon error"
                      onClick={() => handleRemoveParticipante(participante)}
                      title="Remover participante"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="participante-details">
                  <div className="detail-row">
                    <Mail size={16} />
                    <span>{participante.alunos.email || 'Email não informado'}</span>
                  </div>
                  <div className="detail-row">
                    <CreditCard size={16} />
                    <span>RA: {participante.alunos.ra || 'Não informado'}</span>
                  </div>
                  <div className="detail-row">
                    <School size={16} />
                    <span>{participante.alunos.escolas?.nome || 'Escola não definida'}</span>
                  </div>
                  <div className="detail-row">
                    <GraduationCap size={16} />
                    <span>{participante.alunos.anos_escolares?.nome || 'Ano não definido'}</span>
                  </div>
                  <div className="detail-row">
                    <Clock size={16} />
                    <span>Adicionado em: {formatDate(participante.data_adicionado)}</span>
                  </div>
                </div>

                {participante.observacoes && (
                  <div className="participante-observacoes">
                    <p>{participante.observacoes}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Adicionar Participantes */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Adicionar Participantes</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>

            <div className="modal-body">
              {/* Filtros */}
              <div className="filters-section">
                <div className="filters-row">
                  <div className="search-box">
                    <Search size={20} />
                    <input
                      type="text"
                      placeholder="Buscar por nome, email ou RA..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                    />
                  </div>

                  <select
                    value={currentUserProfile?.school_adm && currentUserProfile?.school_id ? currentUserProfile.school_id : filters.escola_id}
                    onChange={(e) => handleFilterChange('escola_id', e.target.value)}
                    disabled={currentUserProfile?.school_adm}
                  >
                    <option value="">Todas as Escolas</option>
                    {escolas.map(escola => (
                      <option key={escola.id} value={escola.id}>{escola.nome}</option>
                    ))}
                  </select>

                  <select
                    value={filters.ano_escolar_id}
                    onChange={(e) => handleFilterChange('ano_escolar_id', e.target.value)}
                  >
                    <option value="">Todos os Anos</option>
                    {anosEscolares.map(ano => (
                      <option key={ano.id} value={ano.id}>{ano.nome}</option>
                    ))}
                  </select>

                  <button className="btn btn-secondary" onClick={clearFilters}>
                    <Filter size={16} />
                    Limpar
                  </button>
                </div>
              </div>

              {/* Seleção de Alunos */}
              <div className="alunos-selection">
                <div className="selection-header">
                  <label className="checkbox-label">
                    <button
                      className="btn-icon"
                      onClick={handleSelectAll}
                      title={selectAll ? 'Desmarcar todos' : 'Selecionar todos'}
                    >
                      {selectAll ? <CheckSquare size={20} /> : <Square size={20} />}
                    </button>
                    <span>
                      {selectedAlunos.length > 0 
                        ? `${selectedAlunos.length} aluno(s) selecionado(s)`
                        : 'Selecionar todos'
                      }
                    </span>
                  </label>
                </div>

                <div className="alunos-list">
                  {alunosDisponiveis.length === 0 ? (
                    <div className="empty-state">
                      <Users size={48} />
                      <h3>Nenhum aluno disponível</h3>
                      <p>Todos os alunos já foram adicionados ou não há alunos que atendam aos filtros.</p>
                    </div>
                  ) : (
                    alunosDisponiveis.map(aluno => (
                      <div 
                        key={aluno.id} 
                        className={`aluno-item ${selectedAlunos.includes(aluno.id) ? 'selected' : ''}`}
                        onClick={() => handleSelectAluno(aluno.id)}
                      >
                        <div className="aluno-checkbox">
                          {selectedAlunos.includes(aluno.id) ? (
                            <CheckSquare size={20} />
                          ) : (
                            <Square size={20} />
                          )}
                        </div>
                        <div className="aluno-info">
                          <h4>{aluno.nome}</h4>
                          <div className="aluno-details">
                            <span>{aluno.email}</span>
                            <span>RA: {aluno.ra}</span>
                            <span>{aluno.escolas?.nome}</span>
                            <span>{aluno.anos_escolares?.nome}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowAddModal(false)}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleAddParticipantes}
                disabled={selectedAlunos.length === 0 || loading}
              >
                {loading ? 'Adicionando...' : `Adicionar ${selectedAlunos.length} Participante(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Participantes;
